import { useState } from "react";
import { motion } from "framer-motion";
import { C } from "../lib/colors";
import { Logo } from "../components/Logo";
import { SpinningO } from "../components/SpinningO";
import { FaqItem } from "../components/FaqItem";
import { submitToFormspree } from "../lib/formspree";
import { IllustrationAgents } from "../components/illustrations/IllustrationAgents";
import { IllustrationPipeline } from "../components/illustrations/IllustrationPipeline";

// Scroll-reveal config shared by every section that animates on entry.
// `once: true` so we don't replay on every scroll past; `-60px` margin
// fires the animation slightly before the element fully enters view so
// the motion is settling by the time the user's eye lands on it.
const inViewProps = {
  initial:    { opacity: 0, y: 28 },
  whileInView:{ opacity: 1, y: 0 },
  viewport:   { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const staggerParent = {
  initial:    "hidden",
  whileInView:"visible",
  viewport:   { once: true, margin: "-60px" },
  variants:   { visible: { transition: { staggerChildren: 0.08 } } },
};
const staggerChild = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

export const LandingPage = ({ onNavigate, authRole }) => {
  const dashboardPage = authRole === "owner" ? "agents" : authRole === "client" ? "portal" : null;
  const dashboardLabel = authRole === "owner" ? "Dashboard →" : authRole === "client" ? "Portal →" : null;
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const handleHomeClick = () => {
    // On landing → reload. Elsewhere this is unreachable since the logo
    // is wrapped per-page; LandingPage's button only renders when on landing.
    window.location.reload();
  };

  const handleSignup = async () => {
    if (!isValidEmail(email)) { setEmailError("Please enter a valid email address."); return; }
    setEmailError(""); setSubmitting(true);
    try {
      await submitToFormspree({ email, _source: "landing-cta" });
      setSubmitted(true);
    } catch (err) {
      setEmailError(err.message || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{background:"#000000",minHeight:"100vh",fontFamily:"'Instrument Sans',sans-serif",color:C.text}}>
      {/* NAV */}
      <nav style={{position:"sticky",top:0,zIndex:100,background:"rgba(6,8,16,0.88)",backdropFilter:"blur(20px)",
        borderBottom:`1px solid ${C.border}`,padding:"0 5%",height:96,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <button onClick={handleHomeClick} style={{background:"none",border:"none",cursor:"pointer",padding:0}} aria-label="Reload home">
          <Logo size={44}/>
        </button>
        <div style={{display:"flex",gap:28}}>
          {["How It Works","Pricing"].map(l=>(
            <button key={l} onClick={()=>onNavigate(l==="How It Works"?"how":"pricing")}
              style={{background:"none",border:"none",color:C.muted,fontSize:14,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",fontWeight:500,transition:"color 0.2s"}}
              onMouseEnter={e=>e.target.style.color="#fff"} onMouseLeave={e=>e.target.style.color=C.muted}>
              {l}
            </button>
          ))}
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {dashboardPage ? (
            <button onClick={()=>onNavigate(dashboardPage)} style={{background:C.green,color:"#000",border:"none",borderRadius:9,
              padding:"9px 22px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",transition:"all 0.2s"}}
              onMouseEnter={e=>e.target.style.background="#fff"} onMouseLeave={e=>e.target.style.background=C.green}>
              {dashboardLabel}
            </button>
          ) : (
            <>
              <button onClick={()=>onNavigate("login")} style={{background:"transparent",color:C.muted,border:`1px solid ${C.border}`,borderRadius:9,
                padding:"8px 18px",fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",transition:"all 0.2s"}}
                onMouseEnter={e=>e.currentTarget.style.color="#fff"} onMouseLeave={e=>e.currentTarget.style.color=C.muted}>
                Log In
              </button>
              <button onClick={()=>onNavigate("onboarding")} style={{background:C.green,color:"#000",border:"none",borderRadius:9,
                padding:"9px 22px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",transition:"all 0.2s"}}
                onMouseEnter={e=>e.target.style.background="#fff"} onMouseLeave={e=>e.target.style.background=C.green}>
                Get Started →
              </button>
            </>
          )}
        </div>
      </nav>

      {/* HERO */}
      <div style={{position:"relative",overflow:"hidden",padding:"100px 5% 80px",textAlign:"center"}}>
        <div style={{position:"absolute",top:"10%",left:"50%",transform:"translateX(-50%)",
          width:700,height:700,borderRadius:"50%",
          background:"radial-gradient(circle,rgba(0,240,160,0.07) 0%,transparent 70%)",pointerEvents:"none"}}/>
        <div style={{position:"absolute",inset:0,backgroundImage:"linear-gradient(rgba(255,255,255,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.02) 1px,transparent 1px)",
          backgroundSize:"52px 52px",maskImage:"radial-gradient(ellipse 80% 60% at 50% 40%,black 40%,transparent 100%)",pointerEvents:"none"}}/>

        <div style={{display:"inline-flex",alignItems:"center",gap:10,background:"rgba(0,240,160,0.08)",
          border:"1px solid rgba(0,240,160,0.2)",color:C.green,padding:"6px 18px 6px 10px",borderRadius:100,
          fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:28,animation:"fadeUp 0.7s ease both"}}>
          <SpinningO size={32} mode="wobble"/>
          Media on Auto Pilot
        </div>

        <h1 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(44px,7vw,82px)",fontWeight:800,
          lineHeight:1.05,letterSpacing:-2,maxWidth:900,margin:"0 auto 22px",animation:"fadeUp 0.7s 0.08s ease both"}}>
          Content that <em style={{fontStyle:"normal",color:C.green}}>ranks.</em><br/>Growth on <em style={{fontStyle:"normal",color:"#fff"}}>auto pilot.</em>
        </h1>
        <p style={{fontSize:"clamp(16px,2vw,19px)",color:C.muted,maxWidth:520,margin:"0 auto 44px",lineHeight:1.7,animation:"fadeUp 0.7s 0.16s ease both"}}>
          AI agents write, optimise, and deliver done-for-you blog posts and social content — so you can focus on running your business.
        </p>
        <div style={{display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap",animation:"fadeUp 0.7s 0.24s ease both"}}>
          <button onClick={()=>onNavigate("onboarding")} style={{background:C.green,color:"#000",border:"none",borderRadius:11,
            padding:"14px 32px",fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",transition:"all 0.2s",display:"flex",alignItems:"center",gap:8}}
            onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform=""}>
            Start Growing Today →
          </button>
          <button onClick={()=>onNavigate("how")} style={{background:"transparent",color:C.text,border:`1px solid ${C.border}`,borderRadius:11,
            padding:"13px 28px",fontSize:15,fontWeight:500,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",transition:"all 0.2s"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=C.border2} onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
            See How It Works
          </button>
        </div>

        {/* Hero Illustrations */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16,maxWidth:680,margin:"60px auto 0",animation:"fadeUp 0.7s 0.32s ease both"}}>
          <div style={{borderRadius:16,overflow:"hidden",border:`1px solid ${C.border}`,height:180}}><IllustrationAgents/></div>
          <div style={{borderRadius:16,overflow:"hidden",border:`1px solid ${C.border}`,height:180}}><IllustrationPipeline/></div>
        </div>

        {/* Proof */}
        <div style={{display:"flex",gap:28,justifyContent:"center",flexWrap:"wrap",marginTop:40,animation:"fadeUp 0.7s 0.4s ease both"}}>
          {["No contracts","First content in 48hrs","Cancel anytime","Human-quality, AI-speed"].map(p=>(
            <div key={p} style={{display:"flex",alignItems:"center",gap:7,color:C.muted,fontSize:13}}>
              <span style={{color:C.green,fontSize:14}}>✓</span> {p}
            </div>
          ))}
        </div>
      </div>

      {/* STATS STRIP — staggered fade-in, each number lifts in turn */}
      <motion.div {...staggerParent}
        style={{background:C.s1,borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,
          padding:"40px 5%",display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:24}}>
        {[{n:"+147%",l:"Avg traffic increase in 90 days"},{n:"48hrs",l:"From signup to first deliverable"},{n:"3.2×",l:"ROI vs traditional agencies"},{n:"500+",l:"Small businesses served"}].map((s,i)=>(
          <motion.div key={i} variants={staggerChild} style={{textAlign:"center",padding:"0 12px"}}>
            <div style={{fontFamily:"'Sora',sans-serif",fontSize:42,fontWeight:800,color:C.green,lineHeight:1,marginBottom:6}}>{s.n}</div>
            <div style={{fontSize:13,color:C.muted}}>{s.l}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* PRICING section removed — the dedicated /pricing page now owns
          tiers + annual toggle + comparison chart + FAQ. The top nav
          and footer both link there. */}

      {/* TESTIMONIALS — moved here from HowItWorksPage. Real social proof
          should sit right before the conversion CTA so prospects see it
          last and ride the trust into the email-capture. Staggered reveal
          + hover lift to feel tactile. */}
      <motion.div {...staggerParent}
        style={{padding:"56px 5%",maxWidth:960,margin:"0 auto",
          display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
        {[
          {quote:"We went from page 4 to page 1 on Google in under 60 days. The content quality is genuinely better than the agency we were paying 3× as much.",name:"Sarah M.",role:"Owner, Sunrise Dental Group",color:C.green},
          {quote:"I was sceptical about AI content but the blogs they deliver are indistinguishable from human-written. Organic leads are up 80% in three months.",name:"Mike R.",role:"CEO, Peak Roofing Co.",color:C.blue},
          {quote:"Media on Auto Pilot handles everything — I just approve and publish. My sphere is growing faster than ever and I haven't written a single word myself.",name:"Lisa T.",role:"Realtor, Luxe Properties",color:C.amber},
        ].map((t,i)=>(
          <motion.div key={i} variants={staggerChild}
            whileHover={{ y: -5, borderColor: `${t.color}45` }}
            transition={{ type: "spring", stiffness: 280, damping: 22 }}
            style={{background:C.s1,border:`1px solid ${C.border}`,borderRadius:16,padding:24,
              willChange:"transform"}}>
            <div style={{color:C.amber,fontSize:13,marginBottom:12,letterSpacing:2}}>★★★★★</div>
            <p style={{fontSize:14,lineHeight:1.75,color:"rgba(255,255,255,0.78)",fontStyle:"italic",marginBottom:18}}>"{t.quote}"</p>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:34,height:34,borderRadius:"50%",background:`${t.color}18`,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:t.color,fontFamily:"'Sora',sans-serif"}}>{t.name[0]}</div>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:"#fff"}}>{t.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{t.role}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* CTA — scales into view; submit button has a spring on hover/press */}
      <div style={{padding:"0 5% 80px"}}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          style={{background:"linear-gradient(135deg,rgba(0,240,160,0.07),rgba(77,170,255,0.05))",
            border:`1px solid rgba(0,240,160,0.18)`,borderRadius:24,padding:"64px 48px",textAlign:"center",position:"relative",overflow:"hidden"}}>
          <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:"clamp(26px,4vw,46px)",fontWeight:800,letterSpacing:-1.5,marginBottom:14}}>
            Ready to grow on autopilot?
          </h2>
          <p style={{color:C.muted,fontSize:16,maxWidth:440,margin:"0 auto 32px"}}>
            Join 500+ businesses using Media on Auto Pilot to rank higher and stay top-of-mind.
          </p>
          <div style={{display:"flex",gap:10,justifyContent:"center",maxWidth:420,margin:"0 auto",flexWrap:"wrap"}}>
            <input value={email} onChange={e=>{setEmail(e.target.value); if(emailError) setEmailError("");}}
              onKeyDown={e=>e.key==="Enter"&&!submitted&&handleSignup()}
              placeholder="Enter your business email…" type="email" disabled={submitted}
              style={{flex:1,minWidth:200,background:"rgba(255,255,255,0.07)",
                border:`1px solid ${emailError?"rgba(255,82,82,0.5)":"rgba(255,255,255,0.15)"}`,
                borderRadius:9,padding:"13px 16px",color:"#fff",fontSize:14,fontFamily:"'Instrument Sans',sans-serif"}}/>
            <motion.button onClick={handleSignup} disabled={submitting||submitted}
              whileHover={!submitting && !submitted ? { scale: 1.03 } : {}}
              whileTap={!submitting && !submitted ? { scale: 0.97 } : {}}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
              style={{background:C.green,color:"#000",border:"none",borderRadius:9,
                padding:"13px 24px",fontSize:14,fontWeight:700,
                cursor:submitting||submitted?"default":"pointer",
                fontFamily:"'Instrument Sans',sans-serif",whiteSpace:"nowrap",
                opacity:submitting?0.7:1}}>
              {submitted ? "✓ You're in!" : submitting ? "Submitting…" : "Claim Free Audit →"}
            </motion.button>
          </div>
          {emailError && (
            <div style={{textAlign:"center",marginTop:10,fontSize:12,color:C.red,
              fontFamily:"'Instrument Sans',sans-serif"}}>
              ⚠ {emailError}
            </div>
          )}
        </motion.div>
      </div>

      {/* FAQ — moved here from HowItWorksPage. Sits after the CTA so
          prospects who weren't convinced first time around can resolve
          objections, then hit the email field a second time. */}
      <motion.div {...inViewProps} style={{maxWidth:680,margin:"0 auto",padding:"20px 5% 80px"}}>
        <h2 style={{fontFamily:"'Sora',sans-serif",fontSize:32,fontWeight:800,letterSpacing:-1,marginBottom:8,textAlign:"center"}}>
          Questions? We've got answers.
        </h2>
        <p style={{color:C.muted,fontSize:15,textAlign:"center",marginBottom:36,lineHeight:1.65}}>
          Everything you need to know before getting started.
        </p>
        {[
          ["How soon will I see my first content?","Your first blog post and social captions will be ready for review within 48 hours of completing your onboarding form. Most clients are publishing within the first week."],
          ["Will the content sound like me?","Yes. Your onboarding form asks about your brand voice, tone, words you love, and words you want to avoid. Every piece is written to match your style — not ours."],
          ["Do I need to know anything about SEO?","Not at all. We handle the keyword research, on-page optimisation, and meta descriptions for you. You just publish and watch your rankings improve."],
          ["What if I don't like a piece of content?","Every plan includes unlimited revisions within 7 days of delivery. Just tell us what to change and we'll rewrite it — no questions asked, no extra charge."],
          ["How much time will this take each month?","Most clients spend 20–30 minutes a month reviewing and approving content in their portal. Everything else — strategy, writing, editing, scheduling — is handled for you."],
          ["Can I cancel if it's not working for me?","Absolutely. No long-term contracts and no cancellation fees. Cancel anytime — you keep access through the rest of the period you've already paid for."],
        ].map(([q,a],i)=>(
          <FaqItem key={i} q={q} a={a}/>
        ))}
      </motion.div>

      {/* FOOTER */}
      <footer style={{borderTop:`1px solid ${C.border}`,padding:"40px 5%"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:32,marginBottom:32}}>
          <div style={{maxWidth:260}}>
            <Logo size={44}/>
            <p style={{fontSize:13,color:C.muted,lineHeight:1.7,marginTop:14}}>
              AI-powered content that ranks, converts, and grows your business — month after month.
            </p>
          </div>
          <div style={{display:"flex",gap:48,flexWrap:"wrap"}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:1.5,marginBottom:14,fontFamily:"'DM Mono',monospace"}}>Company</div>
              {[["How It Works","how"],["Pricing","pricing"],["Contact","contact"]].map(([l,p])=>(
                <button key={p} onClick={()=>onNavigate(p)} style={{display:"block",background:"none",border:"none",
                  color:C.muted,fontSize:13,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",
                  marginBottom:10,padding:0,textAlign:"left",transition:"color 0.2s"}}
                  onMouseEnter={e=>e.target.style.color="#fff"} onMouseLeave={e=>e.target.style.color=C.muted}>{l}</button>
              ))}
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:1.5,marginBottom:14,fontFamily:"'DM Mono',monospace"}}>Legal</div>
              {[["Privacy Policy","privacy"],["Terms of Service","terms"]].map(([l,p])=>(
                <button key={p} onClick={()=>onNavigate(p)} style={{display:"block",background:"none",border:"none",
                  color:C.muted,fontSize:13,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",
                  marginBottom:10,padding:0,textAlign:"left",transition:"color 0.2s"}}
                  onMouseEnter={e=>e.target.style.color="#fff"} onMouseLeave={e=>e.target.style.color=C.muted}>{l}</button>
              ))}
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:1.5,marginBottom:14,fontFamily:"'DM Mono',monospace"}}>Account</div>
              {(dashboardPage
                ? [[dashboardLabel.replace(' →',''), dashboardPage]]
                : [["Log In","login"],["Get Started","onboarding"]]
              ).map(([l,p])=>(
                <button key={p} onClick={()=>onNavigate(p)} style={{display:"block",background:"none",border:"none",
                  color:C.muted,fontSize:13,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",
                  marginBottom:10,padding:0,textAlign:"left",transition:"color 0.2s"}}
                  onMouseEnter={e=>e.target.style.color="#fff"} onMouseLeave={e=>e.target.style.color=C.muted}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:24,display:"flex",alignItems:"center",
          justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.2)"}}>© {new Date().getFullYear()} Media on Auto Pilot — All rights reserved.</div>
          <div style={{display:"flex",gap:16}}>
            {[["Privacy Policy","privacy"],["Terms of Service","terms"],["Contact","contact"]].map(([l,p])=>(
              <button key={p} onClick={()=>onNavigate(p)} style={{background:"none",border:"none",
                color:"rgba(255,255,255,0.2)",fontSize:11,cursor:"pointer",fontFamily:"'Instrument Sans',sans-serif",
                transition:"color 0.2s"}}
                onMouseEnter={e=>e.target.style.color=C.muted} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,0.2)"}>{l}</button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};
